import { Fragment, useState } from "react";
import { PRODUCT_PROFILE, API_KEY_MANAGEMENT  } from "../assets/images/MargotGettingStartedAssets";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { classNames } from "../utils/helpers";
import constants from "../utils/constants";

const StepIds = {
    Products: "Products",
    ProductExplanation: "Product-Explanation",
    CreateProduct: "Create-Product",
    AddPaymentMethod: "Add-Payment-Method",
    ChangeAccountPlan: "Change-Account-Plan",
    PaymentHistory: "Payment-History",
    AccountProfile: "Account-Profile",
    TenantProfile: "Tenant-Profile",
    AddUser: "Add-User",
    StripeCredentials: "Stripe-Credentials",
    SMSTwilio: "SMS-Twilio",
    SMTP: "SMTP",
    SystemTenantAPIKeys: "System-Tenant-API-Keys"
};

const { APP_NAME } = constants;

export default function GettingStarted(){
    const guideSteps = {
        title: "Getting Started",
        children: [
            { 
                id: StepIds.Products,
                title: "Products",
                children: [
                    { id: StepIds.ProductExplanation, title: "Product Explanation"},
                    { id: StepIds.CreateProduct, title: "Create Products"},
                ]
            },
            { 
                id: StepIds.ChangeAccountPlan, 
                title: "Account Plan & Payment History"
            },
            {
                id: StepIds.AccountProfile, 
                title: "Update Account Profile"
            },
            { 
                id: StepIds.TenantProfile,
                title: "Update System Tenant Profile"
            },
            // { 
            //     id: StepIds.AddUser,
            //     title: "Add Users"
            // },
            { 
                title: "Setup System Tenant Credentials",
                children: [
                    { id: StepIds.StripeCredentials, title: "Stripe" },
                    { id: StepIds.SMSTwilio, title: "SMS / Twilio" },
                    { id: StepIds.SMTP, title: "SMTP" },
                ]
            },
            { 
                id: StepIds.SystemTenantAPIKeys,
                title: "System Tenant API Keys"
            },
        ]
    }
    return (
        <Fragment>
            <div className="grid grid-cols-5 divide-x-4 border-4 border-blue-300">
                <div className="col-span-1 p-4 sticky top-12 h-fit">
                    <TreeNode title={guideSteps.title} children={guideSteps.children} />
                </div>
                <div className="col-span-4 p-4">
                    <div className="flex flex-col gap-4 divide-y-4 divide-blue-300">
                        <p className="flex flex-row justify-start text-5xl">Getting Started</p>
                        <Section title="Product Explanation" id={StepIds.ProductExplanation}>
                            <p>
                                Products are defined items made available for purchase and are distinct between each System Tenant. There are two types of products; primary license and feature supplement.    
                            </p>
                            <p>
                                The primary license is always a periodic subscription that renews at a defined interval. It is the main product type that drives {APP_NAME}, and is the primary means of gaining access for a customer. An example would be tiered memberships for streaming platforms where the most basic membership includes ads while the next membership would be ad free.
                            </p>
                            <p>
                                Feature supplements are for one-off purchases that do not renew, they can either expire after a period of time or last indefinitely. An example would be an exclusive skin or mod feature in a video game.
                            </p>
                        </Section>
                        <Section title="Create Products" id={StepIds.CreateProduct}>
                            <p>
                                From the dashboard or the navigation menu you can access the Manage Products page. 
                                This page allows you to view all products that you created. 
                                From there you can soft-delete, edit, and create new products.
                            </p>
                            <img
                                src={PRODUCT_PROFILE}
                                alt={`Product Page Screenshot`}
                                className="w-auto block border-2 shadow-xl"
                            />
                        </Section>
                        {/* <Section title={"Adding a Payment Method"} id={StepIds.AddPaymentMethod}>
                        </Section> */}
                        <Section title={"Account Plan & Payment History"} id={StepIds.ChangeAccountPlan}>
                            <p>
                                Before you can choose your account plan, a stripe customer id must be created by clicking the Creat Stripe Account on the dashboard.
                                Afterwards, you can enter your payment information if needed and manage your account plan on the Account page. A summary of payments made to your system tenant is located at the top left of the page.
                                Located on the bottom of the page is a list of the account's payment history.
                            </p>
                        </Section>
                        <Section title={"Account Profile"} id={StepIds.AccountProfile}>
                            Account details can be accessed via the dashboard or side navigation menu. There you can mange the account details, preferred billing day.
                        </Section>
                        <Section title={"System Tenant Profile"} id={StepIds.TenantProfile}>
                            System tenants serve as environments that managed under a client's account. 
                            On the system tenant's profile page you can change various settings of the tenant, 
                            view api calls made from the client's to {APP_NAME}, 
                            and view automated communications made from {APP_NAME} on the system tenant's behalf.
                        </Section>
                        {/* <Section title={"Add Users"} id={StepIds.AddUser}>
                        </Section> */}
                        <Section title={"System Tenant Credentials"}>
                            <p>
                                Each system tenant has their own set of credentials that can be maintained 
                                by navigating to the system tenant's profile and toggling the Show Credentials button.
                            </p>
                        </Section>
                        <Section title={"Stripe"} id={StepIds.StripeCredentials}>
                            <p>
                                A stripe account must be created in order to accept payments. 
                                Once you have an account you can copy the secret key from the home page 
                                and paste it in the credentials section of the desired system tenant profile. 
                            </p>
                        </Section>
                        <Section title={"SMS / Twilio"} id={StepIds.SMSTwilio}>
                            <p>
                                To send automated text notifications you need to create a Twilio account. 
                                Afterwards, it is necessary to purchase a phone number, it will  be added to your active numbers, and verify it's ready for use.
                                Then you can add the following credentials to your system tenant credentials.
                            </p>
                            <div>
                                <span className="underline">Account Info</span>
                                <ul className="list-disc list-inside">
                                    <li>Account SID</li>
                                    <li>Auth Token</li>
                                </ul>
                            </div>
                            <div>
                                <span className="underline">Active Phone Number Properties</span>
                                <ul className="list-disc list-inside">
                                    <li>Phone Number</li>
                                    <li>Phone Number SID</li>
                                </ul>
                            </div>
                        </Section>
                        <Section title={"SMTP"} id={StepIds.SMTP}>
                            <p>
                                The following credentials are required for automated emailing. AWS Simple Email Service (SES) is recommended.
                            </p>
                            <div>
                                <ul className="list-disc list-inside">
                                    <li>Mail From Address</li>
                                    <li>Mail From Name</li>
                                    <li>Server</li>
                                    <li>Port #</li>
                                    <li>Server Username</li>
                                    <li>Server Password</li>
                                </ul>
                            </div>
                        </Section>
                        <Section title={"System Tenant API Keys"} id={StepIds.SystemTenantAPIKeys}>
                            <p>
                                API Keys can be generated within the web app to communicate {APP_NAME}'s API. 
                                You can generate any number of keys and deactivate keys that are no longer desired.
                            </p>
                            <img
                                src={API_KEY_MANAGEMENT}
                                alt={`Manage API Key Page Screenshot`}
                                className="w-auto block border-2 shadow-xl"
                            />
                        </Section>
                    </div>
                </div>
            </div>
            {/* Necessary for calculated background color in TreeNode */}
            <div className="grid grid-cols-9 hidden">
                <div className="h-10 bg-blue-900"/>
                <div className="h-10 bg-blue-800"/>
                <div className="h-10 bg-blue-700"/>
                <div className="h-10 bg-blue-600"/>
                <div className="h-10 bg-blue-500"/>
                <div className="h-10 bg-blue-400"/>
                <div className="h-10 bg-blue-300"/>
                <div className="h-10 bg-blue-200"/>
                <div className="h-10 bg-blue-100"/>
            </div>            
        </Fragment>
    );
}

function TreeNode({id, title = "", children, gradientValue = 900, startOpen = true}) {
    const [isOpen, setIsOpen] = useState(startOpen);
    const isFirstChildren = gradientValue === 700;
    const gradient = gradientValue >= 100 ? gradientValue : 100;
    const backgrounColor = "bg-blue-" + gradientValue;
    return (
        <div className={title ? "my-2" : ""}>
            <div className={classNames("flex flex-row justify-between content-center p-2 rounded-lg", (isFirstChildren || children) ? `${backgrounColor} h-14 ` : "")}>
                {/* <a href={id ? `#${id}` : "javascript:void(0)"} className={(children || isFirstChildren) ? "text-white" : "text-black"}>{title}</a> */}
                <span 
                    className={classNames((children || isFirstChildren) ? "text-white" : "text-black"
                        , (id ? "hover:text-gray-300 hover:cursor-pointer" : ""))}
                    onClick={() => {
                        if (!id) return;
                        document.getElementById(id)?.scrollIntoView()
                    }}
                >
                    {title}
                </span>
                <span className={title && children ? "hover:cursor-pointer" : "hidden"} onClick={() => setIsOpen(!isOpen)}>
                    {isOpen && children ? <ChevronUpIcon className="h-8 text-white"/> : <ChevronDownIcon className="h-8 text-white"/>}
                </span>
            </div>
            {isOpen 
                && children 
                && children.length 
                && children.map(c => <div className="ml-4"><TreeNode id={c?.id} title={`${c?.title}`} children={c?.children} gradientValue={gradient - 200} startOpen={false}/></div>)}
        </div>
    )
}

function Section({title, children, id}) {
    return (
        <div>
            <div id={id} className="flex flex-row justify-start p-4 text-2xl underline">{title}</div>
            <div className="p-4 flex flex-col gap-4">
                {children}
            </div>
        </div>
    )
}